import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, debounceTime, firstValueFrom, map, Observable, startWith } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Filter } from 'src/app/models/common/filter';
import { Router } from '@angular/router';
import { DatePipe, formatDate } from '@angular/common';
import { NgxSpinnerService } from 'ngx-spinner';
import { SubSink } from 'SubSink';
import { MatDatepicker } from '@angular/material/datepicker';
import { Moment } from 'moment';
import { animate, style, transition, trigger } from '@angular/animations';
import { FilterPresetDto } from 'src/app/models/dtos/filter-preset-dto';
import { FilterTypeEnum } from 'src/app/models/enums/filter-type-enum';
import { UserService } from 'src/app/data/user.service';
import { FilterPresetNameFormDialogComponent } from 'src/app/shared/components/filters/filter-preset-name-form-dialog/filter-preset-name-form-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { buildFilterArray } from 'src/app/common/utils/build-filter-array';
import Comparator from 'src/app/common/comparators/comparator';
import { autocompleteValidator } from 'src/app/shared/validators/autocomplete.validator';
import moment from 'moment';
import { HolidayPerMonthListDataSource } from './holiday-per-month-list.datasource';
import { HolidayPerMonthListFiltersComponent } from '../holiday-per-month-list-filters/holiday-per-month-list-filters.component';
import { daysOfMonth } from 'src/app/common/utils/date.utils';
import { AbsenceStatus } from 'src/app/models/enums/absence-status';
import { DayEnum } from 'src/app/models/enums/day-enum';
import { ScreenService } from 'src/app/core/screen.service';
import { DictionaryService } from 'src/app/data/dictionary.service';

export interface MonthWorkingTime {
  days: number;
  hours: number;
}

@Component({
  selector: 'app-holiday-per-month-list',
  templateUrl: './holiday-per-month-list.component.html',
  styleUrl: './holiday-per-month-list.component.scss',
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('1s ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('1s ease-in', style({ opacity: 0 }))
      ])
    ])
  ],
  standalone: false
})
export class HolidayPerMonthListComponent implements AfterViewInit, OnInit, OnDestroy {
  screenService = inject(ScreenService);
  dataSource = inject(HolidayPerMonthListDataSource);
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private datePipe = inject(DatePipe);
  private spinner = inject(NgxSpinnerService);
  private userService = inject(UserService);
  private dialog = inject(MatDialog);
  private formBuilder = inject(UntypedFormBuilder);
  private dictionaryService = inject(DictionaryService);

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  set currentDate(value: Date) {
    this.currentDate$.next(value);
  }
  get currentDate() {
    return this.currentDate$.value;
  }

  private currentDate$ = new BehaviorSubject<Date>(new Date());
  private holidayDates: Set<string> = new Set();

  filtersFormGroup: UntypedFormGroup;
  filters: Filter[] = [];
  areFiltersExpanded: boolean = false;
  displayedColumns$: Observable<string[]>;


  displayedColumns = ['workerFullName', 'company', 'employer', 'employerObject', 'approvedSum', 'draftSum', 'pendingApprovalSum'];
  days = [];

  private readonly subs = new SubSink();
  public readonly defaultFilterPresetName = 'default';
  public filterPresets: FilterPresetDto[];
  private _selectedFilterPresetId: number;
  public set selectedFilterPresetId(value: number) {
    if (this._selectedFilterPresetId !== value) {
      this._selectedFilterPresetId = value;

      if (value) {
        this.applyFilterPreset(this.filterPresets.find((fp) => fp.Id === value));
      }
    }
  }

  public get AbsenceStatus() {
    return AbsenceStatus;
  }

  public get DayEnum() {
    return DayEnum;
  }

  public get selectedFilterPresetId(): number {
    return this._selectedFilterPresetId;
  }
  public get selectedFilterPreset(): FilterPresetDto {
    return this.filterPresets?.find((fp) => fp.Id === this.selectedFilterPresetId);
  }
  public get showFilterPresetSelector(): boolean {
    return (
      this.filterPresets &&
      (this.filterPresets.length > 1 || (this.filterPresets.length === 1 && this.filterPresets[0].Name !== this.defaultFilterPresetName))
    );
  }
  public get isSavedFilterPreset(): boolean {
    return this.selectedFilterPreset && this.selectedFilterPreset.Name !== this.defaultFilterPresetName;
  }

  constructor() { }

  ngOnInit(): void {
    this.currentDate = moment(this.router.url.replace('/holidays/', ''), 'YYYY/MM').toDate();
    this.fetchHolidayCalendars();
    this.buildFormGroup();
    this.initFiltersFormChangeObserver();
    this.initLoader();

    this.initTabColumns();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  async ngAfterViewInit(): Promise<void> {
    await this.restoreSavedFiltersPreset();
    this.filters = buildFilterArray(this.filtersFormGroup, HolidayPerMonthListFiltersComponent.operatorsMap);
    this.subs.sink = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.subs.sink = this.translateService.onLangChange.subscribe(() => (this.paginator.pageIndex = 0));

    this.subs.sink = combineLatest([
      this.sort.sortChange.pipe(startWith(null)),
      this.paginator.page.pipe(startWith(null)),
      this.translateService.onLangChange.pipe(startWith(null)),
    ]).subscribe(() => this.fetchHolidays());
  }

  previousMonth() {
    this.currentDate = new Date(this.currentDate.addMonths(-1));
    this.setNavigation();
    this.fetchHolidays();
  }

  nextMonth() {
    this.currentDate = new Date(this.currentDate.addMonths(1));
    this.setNavigation();
    this.fetchHolidays();
  }

  onDateChange(event: Moment, picker?: MatDatepicker<Moment>): void {
    picker.close();
    this.currentDate = event.toDate();
    this.setNavigation();
    this.fetchHolidays();
  }

  resetFilters(): void {
    this.filtersFormGroup.reset();
  }

  filterData(filters: Filter[]) {
    this.filters = filters;
    this.fetchHolidays();
  }

  toggleFiltersPanel() {
    this.areFiltersExpanded = !this.areFiltersExpanded;
  }

  isHoliday(date: Date): boolean {
    return this.holidayDates.has(moment(date).format('YYYY-MM-DD'));
  }

  private initLoader() {
    this.subs.sink = this.dataSource.isLoading$.subscribe((isLoading) => (isLoading ? this.spinner.show() : this.spinner.hide()));
  }

  private setNavigation() {
    this.router.navigateByUrl(`holidays/${this.datePipe.transform(this.currentDate, 'yyyy/MM')}`);
  }

  private fetchHolidayCalendars() {
    this.dictionaryService.getHolidayCalendars().subscribe((holidays) => {
      this.holidayDates = new Set(holidays.map((date) => moment(date).format('YYYY-MM-DD')));
    });
  }

  private fetchHolidays(): void {
    this.dataSource.pageOptionsSubject.next({
      Year: this.currentDate.getFullYear(),
      Month: this.currentDate.getMonth() + 1,
      Page: this.paginator.pageIndex + 1,
      PageSize: this.paginator.pageSize,
      SortingField: this.sort.active,
      SortingDirection: this.sort.direction,
      Filters: this.filters,
    });
  }

  private applyFilterPreset(filterPreset) {
    const filters = filterPreset?.Object;

    if (filters && Object.values(filters).some((v) => !!v)) {
      this.filtersFormGroup.reset(undefined, { emitEvent: false });
      this.filtersFormGroup.patchValue(filters, { emitEvent: false });
      this.areFiltersExpanded = this.areFiltersExpanded || this.selectedFilterPreset.Name === this.defaultFilterPresetName;
    }
  }

  private async upsertFilterPreset(): Promise<void> {
    const selectedPreset = this.filterPresets.find(
      (fp) => (this.selectedFilterPresetId && this.selectedFilterPresetId === fp.Id) || fp.Name === this.defaultFilterPresetName,
    );

    const filters = this.nonNullValues(this.filtersFormGroup.getRawValue());

    if (selectedPreset) {
      if (filters) {
        await firstValueFrom(
          this.userService.updateFilterPreset(selectedPreset.Id, {
            Name: selectedPreset.Name,
            FilterPresetObject: filters,
          }),
        );
      } else {
        await firstValueFrom(this.userService.deleteFilterPreset(selectedPreset.Id));
      }
    } else if (filters) {
      await firstValueFrom(
        this.userService.addFilterPreset({
          Name: this.defaultFilterPresetName,
          FilterTypeId: FilterTypeEnum.HolidayPerMonthList,
          FilterPresetObject: filters,
        }),
      );
    } else {
      return;
    }
    this.filterPresets = await firstValueFrom(this.userService.getFilterPresets(FilterTypeEnum.HolidayPerMonthList));
    this.selectedFilterPresetId = this.filterPresets?.find((fp) => fp.IsSelected)?.Id;
  }

  private nonNullValues(obj): { [key: string]: unknown } {
    const properties = Object.entries(obj).filter(([key, value]) => value !== null && value && (!(value instanceof Array) || value.length));
    if (properties.length) {
      return Object.fromEntries(properties);
    }
  }

  private async restoreSavedFiltersPreset() {
    this.filterPresets = await firstValueFrom(this.userService.getFilterPresets(FilterTypeEnum.HolidayPerMonthList));

    if (this.filterPresets && this.filterPresets.length) {
      const selectedPreset = this.filterPresets.find((fp) => fp.IsSelected);

      this.selectedFilterPresetId = selectedPreset?.Id;
    }
  }

  public async saveFilterPresetButtonClick(): Promise<void> {
    if (this.isSavedFilterPreset) {
      const existingDefaultPreset = this.filterPresets.find((fp) => fp.Name == this.defaultFilterPresetName && this.selectedFilterPresetId !== fp.Id);

      if (existingDefaultPreset) {
        await firstValueFrom(this.userService.deleteFilterPreset(existingDefaultPreset.Id));
      }

      this.selectedFilterPreset.Name = this.defaultFilterPresetName;
      await this.upsertFilterPreset();
    } else {
      var res = await firstValueFrom(this.dialog.open(FilterPresetNameFormDialogComponent, { panelClass: 'form-dialog' }).afterClosed());

      if (res) {
        if (this.selectedFilterPreset?.Name === this.defaultFilterPresetName) {
          this.selectedFilterPreset.Name = res;
        }
        await this.upsertFilterPreset();
      }
    }
  }

  public async setSelectedFilterPresetId(value: number): Promise<void> {
    this.selectedFilterPresetId = value;
    await this.filterData(buildFilterArray(this.filtersFormGroup, HolidayPerMonthListFiltersComponent.operatorsMap));
    await firstValueFrom(this.userService.selectFilterPreset(value));
  }

  private buildFormGroup(): void {
    this.filtersFormGroup = this.formBuilder.group({
      companyId: [null],
      company: [null, [autocompleteValidator]],
      employerId: [null],
      employer: [null, [autocompleteValidator]],
      employerObjectId: [null],
      employerObject: [null, [autocompleteValidator]],
      pesel: [null],
      firstName: [null],
      lastName: [null],
      agreementStatusId: [null],
      absenceStatusId: [null],
    });
  }

  private initFiltersFormChangeObserver() {
    const comparator = new Comparator<any>();

    this.subs.sink = this.filtersFormGroup.valueChanges.pipe(debounceTime(1000)).subscribe(async () => {
      if (this.filtersFormGroup.invalid) {
        return;
      }

      const filters = this.nonNullValues(this.filtersFormGroup.getRawValue());

      const selectedFilterPresetId = this.filterPresets.find((fp) => comparator.equals(fp.Object, filters))?.Id;

      if (!selectedFilterPresetId) {
        this.selectedFilterPresetId = this.filterPresets.find((fp) => fp.Name === this.defaultFilterPresetName)?.Id;

        await this.upsertFilterPreset();
      } else {
        this.selectedFilterPresetId = selectedFilterPresetId;
      }
    });
  }

  private initTabColumns() {
    this.displayedColumns$ = this.currentDate$.pipe(
      map((currentDate) => {
        return [
          'workerFullName',
          'company',
          'employer',
          'employerObject',
          ...daysOfMonth(currentDate).map((date) => formatDate(date, 'dd.MM', 'en')),
          'approvedSum',
          'draftSum',
          'pendingApprovalSum',
        ];
      }),
    );

    this.displayedColumns$.subscribe((d) => {
      this.displayedColumns = [...d];
      this.days = daysOfMonth(this.currentDate).map((date) => ({ label: formatDate(date, 'dd.MM', 'en'), date }));
    });
  }
}
